import React, { useEffect, useRef, useState } from "react";
import { ASSETS } from "@/lib/content";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { useSiteCopy } from "@/lib/useSiteCopy";
import { useTenant } from "@/lib/TenantContext";
import { buildNavModel, resolveBrandName } from "@/lib/siteLinks";
import { Menu, X, User, ChevronDown } from "lucide-react";

/**
 * Inline link capacity for the two desktop bands, below `xl:hidden` mobile hamburger is
 * used instead (BUG-045).
 *
 * The model can hand this header up to 11 links (5 `ANCHOR_SECTIONS` + 6 `INTERIOR_PAGES`,
 * lib/siteLinks.js) and French labels are the longest: "Mon parcours", "Témoignages",
 * "Programmes", "Réserver"... A rough width budget at each band's own minimum
 * viewport, with `max-w-7xl mx-auto px-6 md:px-10` and the bar's `gap-4` between logo / nav
 * / right-controls:
 *   - fixed overhead (padding + bar gaps + logo + language toggle + account link + the
 *     "Réserver un appel" CTA) is ~620-650px on both bands (none of those are responsive);
 *   - at 1024px (lg) that leaves ~380px for nav content — not even 4 average-length French
 *     links plus their `gap-6` fit, let alone 11;
 *   - at 1280px (xl, `gap-8`) it leaves ~660px — still short of the ~990px the full 11
 *     would need, so "just widen the switch-on breakpoint" cannot work for the worst case.
 * Conclusion: a fixed inline count per band, with the remainder in the "More" disclosure
 * below, rather than hoping the whole list fits past some breakpoint. Counts are picked
 * with margin under that budget (3 links + the More pill comfortably clear 380px; 6 links +
 * More clear 660px) — a tenant with fewer links than the count in play simply never renders
 * the disclosure, so the common case (most tenants have far fewer than 11) shows every link
 * inline with nothing hidden.
 *
 * Which count is "in play" is read from `window.matchMedia`, not from a second, CSS-only
 * copy of the nav: with a single JS-computed split, a link is either inline or in the
 * overflow list, never both, so there is exactly one element (and one `data-testid`) per
 * link at any moment — no duplicate, half-hidden nodes for assistive tech to trip over.
 * `matchMedia` is unavailable under jsdom; the effect below no-ops there and the component
 * keeps its lg-band default, which is what the tests exercise.
 */
const LG_INLINE_COUNT = 3; // 1024-1279px band
const XL_INLINE_COUNT = 6; // >=1280px band

/**
 * Classic's header (template t1_classic).
 *
 * Its destinations are NOT its own: they are the shared nav model (lib/siteLinks.js), the
 * same derivation the five themed headers and all six footers render. Before that model
 * existed, each chrome kept its own list, and the five themes' lists had drifted down to
 * four same-page anchors plus a Book button — no interior page was reachable from a themed
 * site's navigation at all (BUG-006). One derivation, six renderers, no drift.
 *
 * Two behaviours the model owns and this file therefore no longer decides:
 *   - the /learn entry appears only when `tenant.has_published_courses === true` (BUG-007);
 *   - the /parcours entry appears only when `tenant.has_active_pathways === true`;
 *   - the journal is linked as the `/journal` ARCHIVE page, not as the homepage `#blog`
 *     anchor, so it is one entry and not two.
 *
 * @param {object} [toggles] `tenant.section_toggles`; a section anchor is listed unless
 *   the key is exactly `false`. The homepage passes the same object its sections are gated
 *   on; ThemedPageLayout passes the tenant's own on interior pages.
 */
const Navbar = ({ toggles = {} }) => {
  const { lang, setLang, override } = useLanguage();
  const siteCopy = useSiteCopy();
  const tt = useT("templates");
  const { tenant } = useTenant();
  const logo = override("site.logo") || ASSETS.logo;
  // The logo's alt text used to be the hardcoded string "Emilie Houde Naturopathe" — the
  // SEED tenant's name, announced by every screen reader on every practitioner's site.
  const brandName = resolveBrandName(tenant, tt("shared.brand.fallback"));
  const nav = buildNavModel({ siteCopy, tenant, toggles, brand: brandName });
  const links = [...nav.sections, ...nav.pages];
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  // Which of the two desktop capacities is active — see the comment on LG_INLINE_COUNT.
  const [isXl, setIsXl] = useState(() =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia("(min-width: 1280px)").matches
      : false,
  );
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const query = window.matchMedia("(min-width: 1280px)");
    const onChange = (e) => setIsXl(e.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);

  const visibleCount = isXl ? XL_INLINE_COUNT : LG_INLINE_COUNT;
  const inlineLinks = links.slice(0, visibleCount);
  const overflowLinks = links.slice(visibleCount);

  const [moreOpen, setMoreOpen] = useState(false);
  const moreRef = useRef(null);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const onOutsideClick = (e) => {
      if (moreRef.current && !moreRef.current.contains(e.target)) setMoreOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("mousedown", onOutsideClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onOutsideClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [moreOpen]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "backdrop-blur-xl bg-[#C9D2BD]/85 border-b border-eh-border" : ""
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 flex items-center justify-between gap-4 py-4">
        <a href={nav.home} data-testid="logo-home" className="flex items-center gap-2 shrink-0">
          <img src={logo} alt={brandName} className="h-16 md:h-20 w-auto" />
        </a>

        <nav className="hidden lg:flex items-center justify-center gap-6 xl:gap-8 flex-1">
          {inlineLinks.map((l) => (
            <a
              key={l.key}
              href={l.href}
              data-testid={`nav-link-${l.key}`}
              className="text-sm font-medium text-eh-ink/80 hover:text-eh-ink transition-colors whitespace-nowrap"
            >
              {l.label}
            </a>
          ))}
          {overflowLinks.length > 0 && (
            <div ref={moreRef} className="relative shrink-0">
              <button
                type="button"
                data-testid="nav-more-toggle"
                aria-expanded={moreOpen}
                aria-controls="nav-more-menu"
                onClick={() => setMoreOpen((v) => !v)}
                className="flex items-center gap-1 text-sm font-medium text-eh-ink/80 hover:text-eh-ink transition-colors whitespace-nowrap"
              >
                {tt("shared.nav.more")}
                <ChevronDown size={14} className={`transition-transform ${moreOpen ? "rotate-180" : ""}`} />
              </button>
              {moreOpen && (
                <div
                  id="nav-more-menu"
                  data-testid="nav-more-menu"
                  className="absolute left-1/2 top-full mt-3 min-w-[200px] -translate-x-1/2 rounded-2xl border border-eh-border-soft bg-eh-cream py-2 shadow-xl z-50"
                >
                  {overflowLinks.map((l) => (
                    <a
                      key={l.key}
                      href={l.href}
                      data-testid={`nav-link-${l.key}`}
                      onClick={() => setMoreOpen(false)}
                      className="block px-4 py-2 text-sm font-medium text-eh-ink/80 whitespace-nowrap hover:bg-eh-cream-deep hover:text-eh-ink transition-colors"
                    >
                      {l.label}
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}
        </nav>

        <div className="flex items-center gap-3 shrink-0">
          <div
            data-testid="language-toggle"
            className="hidden md:flex items-center gap-1 text-xs font-semibold tracking-wider"
          >
            <button
              data-testid="lang-fr"
              onClick={() => setLang("fr")}
              className={`px-2 py-1 rounded-full transition ${
                lang === "fr" ? "bg-eh-ink text-eh-bg" : "text-eh-muted hover:text-eh-ink"
              }`}
            >
              FR
            </button>
            <span className="text-eh-border">/</span>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2 py-1 rounded-full transition ${
                lang === "en" ? "bg-eh-ink text-eh-bg" : "text-eh-muted hover:text-eh-ink"
              }`}
            >
              EN
            </button>
            <span className="text-eh-border">/</span>
            <button
              data-testid="lang-es"
              onClick={() => setLang("es")}
              className={`px-2 py-1 rounded-full transition ${
                lang === "es" ? "bg-eh-ink text-eh-bg" : "text-eh-muted hover:text-eh-ink"
              }`}
            >
              ES
            </button>
          </div>
          {nav.account && (
            <a href={nav.account.href} data-testid="nav-client-portal" className="hidden md:inline-flex items-center gap-1.5 text-sm font-medium text-eh-ink/80 hover:text-eh-ink transition-colors whitespace-nowrap">
              <User size={16} /> {nav.account.label}
            </a>
          )}
          {nav.book && (
            <a href={nav.book.href} data-testid="nav-book-cta" className="hidden md:inline-flex btn-primary text-sm whitespace-nowrap">
              {nav.book.label}
            </a>
          )}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 rounded-full border border-eh-border"
            onClick={() => setOpen((v) => !v)}
            aria-label={open ? tt("shared.nav.closeMenu") : tt("shared.nav.openMenu")}
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-eh-bg border-t border-eh-border">
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-4">
            {links.map((l) => (
              <a
                key={l.key}
                href={l.href}
                onClick={() => setOpen(false)}
                className="text-base font-medium text-eh-ink py-1"
              >
                {l.label}
              </a>
            ))}
            {nav.account && (
              <a href={nav.account.href} onClick={() => setOpen(false)} data-testid="nav-client-portal-mobile" className="inline-flex items-center gap-2 text-base font-medium text-eh-ink py-1">
                <User size={16} /> {nav.account.label}
              </a>
            )}
            <div className="flex items-center justify-between pt-3 border-t border-eh-border">
              <div className="flex items-center gap-2 text-xs font-semibold">
                <button
                  data-testid="lang-fr-mobile"
                  onClick={() => setLang("fr")}
                  className={`px-2 py-1 rounded-full ${lang === "fr" ? "bg-eh-ink text-eh-bg" : "text-eh-muted"}`}
                >
                  FR
                </button>
                <button
                  data-testid="lang-en-mobile"
                  onClick={() => setLang("en")}
                  className={`px-2 py-1 rounded-full ${lang === "en" ? "bg-eh-ink text-eh-bg" : "text-eh-muted"}`}
                >
                  EN
                </button>
                <button
                  data-testid="lang-es-mobile"
                  onClick={() => setLang("es")}
                  className={`px-2 py-1 rounded-full ${lang === "es" ? "bg-eh-ink text-eh-bg" : "text-eh-muted"}`}
                >
                  ES
                </button>
              </div>
              {nav.book && (
                <a href={nav.book.href} onClick={() => setOpen(false)} className="btn-primary text-sm">
                  {nav.book.label}
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
